import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { hasAgentAssistAccess } from '@/reducers/auth/currentUser.redux'
import { connectCCSocket, disconnectCCSocket } from '@/reducers/commandCenter/commandCenter.redux'
import { fetchAgentsWithCallStatus } from '@/reducers/commandCenter/commandCenter.actions'
import { selectOpenChats } from '@/reducers/realtime/realtimeChats.redux'

// Live Listen needs the roster; the transcript needs the relay. Nothing else
// does.
const LIVE_LISTEN_PATHS = ['/live-listen', '/command-center']

/**
 * Owns the Command Center socket, and decides when it is worth having.
 *
 * The socket used to belong to the Live Listen page, so everything riding on it
 * — the transcript relay, the "is this agent on a call" roster behind Listen —
 * died the moment the supervisor navigated away. The widget is global, so the
 * socket has to outlive the page (QCER-1098).
 *
 * It does NOT follow that every logged-in user should hold one. Each socket
 * subscribes to the caller's whole team, so connecting at login turned idle
 * sessions into sustained rtc load. Connect only while something is actually
 * consuming it — which, off Live Listen, means an open transcript or an open
 * chat.
 *
 * Gated on the chat surface, not the live one: the socket carries chat for
 * managers with no RTC socket, and it must exist for them wherever chat does.
 * It also carries the transcript, but that is gated at the surface
 * (`hasLiveListenAccess` in `ChatPanel`) — an org with Live Listen off never
 * opens the panel that would watch one, so the relay stays idle rather than
 * being denied a socket it needs for chat.
 *
 * The roster is seeded once the socket comes up; after that the socket's own
 * CALL_START / CALL_STOP events keep each row current (see `ccSocketMiddleware`).
 */
const CommandCenterSocketProvider = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const currentUser = useSelector((state) => state.currentUser)
  const currentOrganization = useSelector((state) => state.currentOrganization)
  const { socketOpen } = useSelector((state) => state.commandCenter)
  const chats = useSelector((state) => state.realtimeChats?.chats)
  const transcriptOpen = useSelector((state) => state.liveTranscripts?.open)

  const onLiveListen = LIVE_LISTEN_PATHS.includes(location?.pathname)
  // An open chat needs this socket even for a manager whose CHAT arrives on the
  // RTC socket: the roster it feeds is the panel's only source of "is this
  // agent on a call" when no live alert exists (`ChatPanel`'s `isOnCall`), and
  // without it the transcript button never renders — which cannot then open the
  // transcript that would have connected the socket.
  const chatNeedsThisSocket = !isEmpty(selectOpenChats(chats))
  // The transcript has no other transport, whoever the manager is.
  const widgetInUse = Boolean(transcriptOpen) || chatNeedsThisSocket

  const shouldConnect =
    Boolean(currentUser?.token) &&
    hasAgentAssistAccess(currentOrganization) &&
    (onLiveListen || widgetInUse)

  useEffect(() => {
    if (shouldConnect && !socketOpen) {
      dispatch(connectCCSocket())
    }

    if (!shouldConnect && socketOpen) {
      dispatch(disconnectCCSocket())
    }
  }, [shouldConnect, socketOpen, dispatch])

  useEffect(() => {
    if (shouldConnect && socketOpen) {
      // Silent: a failed prefetch must not throw a toast over an unrelated
      // screen. Live Listen still surfaces its own.
      dispatch(fetchAgentsWithCallStatus({ silent: true }))
    }
  }, [shouldConnect, socketOpen, dispatch])

  return null
}

export default CommandCenterSocketProvider
